import { addDays, inNewYork, istAt, noonOf, todayInIst, weekdayOf, yearOf } from './when.ts';
// Imported by the browser: keep server-only modules out.

export const VENUES = ['NSE', 'NFO', 'MCX'] as const;
export type Venue = (typeof VENUES)[number];

export const VENUE_LABEL: Record<Venue, string> = {
  NSE: 'NSE cash',
  NFO: 'NSE F&O',
  MCX: 'MCX',
};

export const DAY_KEEPS = ['SHUT', 'EVENING_ONLY', 'MORNING_ONLY', 'SPECIAL'] as const;
type DayKeep = (typeof DAY_KEEPS)[number];

export const KEEP_LABEL: Record<DayKeep, string> = {
  SHUT: 'Shut',
  EVENING_ONLY: 'Evening only',
  MORNING_ONLY: 'Morning only',
  SPECIAL: 'Special session',
};

export const MARKET_DAY_SOURCES = ['NSE_HOLIDAYS', 'NSE_CIRCULAR'] as const;
type MarketDaySource = (typeof MARKET_DAY_SOURCES)[number];

/** IST `HH:MM`. */
export interface Window {
  opens: string;
  closes: string;
}

export interface MarketDay {
  venue: Venue;
  day: string;
  keep: DayKeep;
  name: string;
  /** SPECIAL only; empty while the exchange has not said when. */
  windows: Window[];
  source: MarketDaySource;
  reference: string | null;
}

/** NORMAL is the only phase the desk sends orders in. */
export const PHASES = ['PRE_OPEN', 'NORMAL', 'CLOSING_AUCTION', 'POST_CLOSE'] as const;
type Phase = (typeof PHASES)[number];

export const PHASE_LABEL: Record<Phase, string> = {
  PRE_OPEN: 'Pre-open',
  NORMAL: 'Normal market',
  CLOSING_AUCTION: 'Closing auction',
  POST_CLOSE: 'Post-close',
};

export interface SessionPhase {
  phase: Phase;
  from: Date;
  to: Date;
}

export interface Session {
  venue: Venue;
  day: string;
  open: Date;
  close: Date;
  phases: SessionPhase[];
  name: string | null;
}

export interface MarketCalendar {
  session(venue: Venue, day: string): Session | null;
  kept(venue: Venue, day: string): MarketDay | null;
  /** False until NSE has put out the year's list: its weekdays are then only presumed open. */
  published(venue: Venue, year: number): boolean;
  days(): readonly MarketDay[];
}

type Hours = { phase: Phase; from: string; to: string }[];

const MCX_HALF_IST = '17:00';

/**
 * Newest first, each from the day a circular made it so. The cash market's continuous
 * session ends at 15:15 for a closing auction on F&O stocks (NSE/CMTR/74466), which also
 * moved F&O's close to 15:40 (NSE/FAOP/74467). The F&O pre-open is futures only.
 */
const ORDINARY: Record<Exclude<Venue, 'MCX'>, { from: string; hours: Hours }[]> = {
  NSE: [
    {
      from: '2026-08-03',
      hours: [
        { phase: 'PRE_OPEN', from: '09:00', to: '09:15' },
        { phase: 'NORMAL', from: '09:15', to: '15:15' },
        { phase: 'CLOSING_AUCTION', from: '15:15', to: '15:35' },
        { phase: 'POST_CLOSE', from: '15:50', to: '16:00' },
      ],
    },
    {
      from: '',
      hours: [
        { phase: 'PRE_OPEN', from: '09:00', to: '09:15' },
        { phase: 'NORMAL', from: '09:15', to: '15:30' },
        { phase: 'POST_CLOSE', from: '15:40', to: '16:00' },
      ],
    },
  ],
  NFO: [
    {
      from: '2026-08-03',
      hours: [
        { phase: 'PRE_OPEN', from: '09:00', to: '09:15' },
        { phase: 'NORMAL', from: '09:15', to: '15:40' },
      ],
    },
    {
      from: '',
      hours: [
        { phase: 'PRE_OPEN', from: '09:00', to: '09:15' },
        { phase: 'NORMAL', from: '09:15', to: '15:30' },
      ],
    },
  ],
};

const MCX_OPEN_IST = '09:00';
/** The MCX close chases the NYMEX settlement, so it follows the US daylight rule. */
const MCX_CLOSE_US_DAYLIGHT_IST = '23:30';
const MCX_CLOSE_US_STANDARD_IST = '23:55';

/** Bounded because a published calendar could shut every day the walk reaches. */
export const CALENDAR_WALK_DAYS = 21;

export const isWeekend = (day: string): boolean => [0, 6].includes(weekdayOf(day));

/**
 * New York turns its clocks at 02:00 on a Sunday, when MCX is shut, and before noon UTC, when it
 * reads 08:00 on daylight time.
 */
export const usDaylight = (day: string): boolean => inNewYork(noonOf(day)).minutes === 8 * 60;

function ordinaryHours(venue: Venue, day: string): Hours {
  if (venue === 'MCX') {
    const closes = usDaylight(day) ? MCX_CLOSE_US_DAYLIGHT_IST : MCX_CLOSE_US_STANDARD_IST;
    return [{ phase: 'NORMAL', from: MCX_OPEN_IST, to: closes }];
  }
  return (ORDINARY[venue].find((rule) => day >= rule.from) ?? { hours: [] }).hours;
}

const normalOf = (hours: Hours) => hours.filter((one) => one.phase === 'NORMAL');

function hoursOf(venue: Venue, day: string, kept: MarketDay | undefined): Hours {
  const ordinary = ordinaryHours(venue, day);
  if (kept === undefined) return isWeekend(day) ? [] : ordinary;
  const [first] = normalOf(ordinary);
  switch (kept.keep) {
    case 'SHUT':
      return [];
    case 'EVENING_ONLY':
      return first ? [{ phase: 'NORMAL', from: MCX_HALF_IST, to: first.to }] : [];
    case 'MORNING_ONLY':
      return first ? [{ phase: 'NORMAL', from: first.from, to: MCX_HALF_IST }] : [];
    case 'SPECIAL':
      return kept.windows.map((window) => ({
        phase: 'NORMAL',
        from: window.opens,
        to: window.closes,
      }));
  }
}

/** A circular's session stands over the holiday list's entry for the same day. */
const outranks = (one: MarketDay, other: MarketDay | undefined): boolean =>
  other === undefined || one.source === 'NSE_CIRCULAR';

export function marketCalendar(days: readonly MarketDay[]): MarketCalendar {
  const kept = new Map<string, MarketDay>();
  for (const one of days) {
    const key = `${one.venue}/${one.day}`;
    if (outranks(one, kept.get(key))) kept.set(key, one);
  }
  const years = new Set(
    days
      .filter((one) => one.source === 'NSE_HOLIDAYS')
      .map((one) => `${one.venue}/${yearOf(one.day)}`),
  );
  return {
    session(venue, day) {
      const found = kept.get(`${venue}/${day}`);
      const hours = hoursOf(venue, day, found);
      const normal = normalOf(hours);
      const first = normal[0];
      const last = normal.at(-1);
      if (first === undefined || last === undefined) return null;
      return {
        venue,
        day,
        open: istAt(day, first.from),
        close: istAt(day, last.to),
        phases: hours.map((one) => ({
          phase: one.phase,
          from: istAt(day, one.from),
          to: istAt(day, one.to),
        })),
        name: found?.name ?? null,
      };
    },
    kept: (venue, day) => kept.get(`${venue}/${day}`) ?? null,
    published: (venue, year) => years.has(`${venue}/${year}`),
    days: () => days,
  };
}

export const venueOfMarket = (market: string): Venue => (market === 'COMMODITY' ? 'MCX' : 'NFO');

/** BSE and BFO keep NSE's and NFO's days; the desk lists neither. */
export function venueOfExchange(exchange: string): Venue {
  const named = exchange.toUpperCase();
  if (named === 'MCX') return 'MCX';
  return named === 'NFO' || named === 'BFO' ? 'NFO' : 'NSE';
}

export const isTradingDay = (calendar: MarketCalendar, venue: Venue, day: string): boolean =>
  calendar.session(venue, day) !== null;

/** The next day any venue trades after `today`, so what falls on a weekend is heard on the Friday. */
export function nextSessionDay(calendar: MarketCalendar, today: string): string {
  let day = addDays(today, 1);
  for (let step = 1; step < CALENDAR_WALK_DAYS; step += 1) {
    if (VENUES.some((venue) => isTradingDay(calendar, venue, day))) break;
    day = addDays(day, 1);
  }
  return day;
}

export const TRADED_VENUES = ['NFO', 'MCX'] as const satisfies readonly Venue[];

export const hadSession = (calendar: MarketCalendar, day: string): boolean =>
  TRADED_VENUES.some((venue) => isTradingDay(calendar, venue, day));

export const phaseAt = (session: Session | null, now: Date): SessionPhase | null =>
  session?.phases.find((one) => one.from <= now && now < one.to) ?? null;

/** The close itself still counts as trading, so a bell-time reading belongs to the session. */
export const isShut = (calendar: MarketCalendar, venue: Venue, now: Date): boolean =>
  !(calendar.session(venue, todayInIst(now))?.phases ?? []).some(
    (one) => one.phase === 'NORMAL' && one.from <= now && now <= one.to,
  );

function walk(
  calendar: MarketCalendar,
  venue: Venue,
  from: string,
  step: 1 | -1,
  found: (session: Session) => boolean,
): Session | null {
  let day = from;
  for (let steps = 0; steps <= CALENDAR_WALK_DAYS; steps += 1) {
    const session = calendar.session(venue, day);
    if (session && found(session)) return session;
    day = addDays(day, step);
  }
  return null;
}

export const lastClose = (calendar: MarketCalendar, venue: Venue, now: Date): Session | null =>
  walk(calendar, venue, todayInIst(now), -1, (session) => session.close <= now);

export const nextOpen = (calendar: MarketCalendar, venue: Venue, now: Date): Session | null =>
  walk(calendar, venue, todayInIst(now), 1, (session) => session.open > now);

export const previousTradingDay = (
  calendar: MarketCalendar,
  venue: Venue,
  day: string,
): string | null => walk(calendar, venue, addDays(day, -1), -1, () => true)?.day ?? null;

/** The venue's next session after `day`, within the calendar's walk. */
export function nextTradingDay(calendar: MarketCalendar, venue: Venue, day: string): string | null {
  for (let step = 1; step <= CALENDAR_WALK_DAYS; step += 1) {
    const next = addDays(day, step);
    if (isTradingDay(calendar, venue, next)) return next;
  }
  return null;
}

/** The close of the session `sessions` trading days on from `today`, today's at none. */
export function closeAfter(
  calendar: MarketCalendar,
  venue: Venue,
  today: string,
  sessions: number,
): string | null {
  if (sessions <= 0) return calendar.session(venue, today)?.close.toISOString() ?? null;
  let seen = 0;
  for (let step = 1; step <= CALENDAR_WALK_DAYS; step += 1) {
    const session = calendar.session(venue, addDays(today, step));
    if (session === null) continue;
    seen += 1;
    if (seen === sessions) return session.close.toISOString();
  }
  return null;
}

/** The first `count` days from `today` on that a traded venue has a session. */
export function sessionDaysAhead(calendar: MarketCalendar, today: string, count: number): string[] {
  const days: string[] = [];
  for (let step = 0; days.length < count && step <= 3 * count; step += 1) {
    const day = addDays(today, step);
    if (TRADED_VENUES.some((venue) => calendar.session(venue, day) !== null)) days.push(day);
  }
  return days;
}

export function overnight(
  calendar: MarketCalendar,
  venue: Venue,
  now: Date,
): { from: Session; to: Session } | null {
  if (!isShut(calendar, venue, now)) return null;
  const from = lastClose(calendar, venue, now);
  const to = nextOpen(calendar, venue, now);
  return from && to ? { from, to } : null;
}

/**
 * Trading days in `(from, to]`, so 0 on expiry day. Past the walk horizon this is a lower
 * bound, never null: null means the contract has no expiry.
 */
export function sessionsUntil(
  calendar: MarketCalendar,
  venue: Venue,
  from: string,
  to: string,
): number {
  if (to <= from) return 0;
  let sessions = 0;
  let day = addDays(from, 1);
  for (let step = 0; step < CALENDAR_WALK_DAYS; step += 1) {
    if (isTradingDay(calendar, venue, day)) sessions += 1;
    if (day >= to) return sessions;
    day = addDays(day, 1);
  }
  return sessions;
}

export const sessionsToExpiry = (
  calendar: MarketCalendar,
  expiry: string | null,
  market: string,
  now: Date,
): number | null =>
  expiry === null ? null : sessionsUntil(calendar, venueOfMarket(market), todayInIst(now), expiry);

/** Sessions left in which the broker still trades the contract, its tender period taken off. */
export const sessionsToLast = (
  calendar: MarketCalendar,
  venue: Venue,
  today: string,
  expiry: string,
  contract: string,
): number => sessionsUntil(calendar, venue, today, expiry) - tenderSessions(venue, contract);

/** Zerodha's sheet of MCX contracts by settlement; every other one is delivered. */
const CASH_SETTLED_MCX = new Set([
  'CRUDEOIL',
  'CRUDEOILM',
  'NATURALGAS',
  'NATGASMINI',
  'ELECDMBL',
  'SUNOIL',
]);

/**
 * The last sessions before expiry, expiry day included, that Zerodha will not trade: it squares off a
 * delivered MCX contract, agri too, the session before its tender period. An unknown contract counts
 * as delivered, so its warning comes early rather than late.
 */
function tenderSessions(venue: Venue, contract: string): number {
  return venue !== 'MCX' || CASH_SETTLED_MCX.has(contract) ? 0 : 3;
}

export const EXPIRY_CONTRACTS = [
  ...[
    'CRUDEOIL',
    'CRUDEOILM',
    'GOLD',
    'GOLDM',
    'SILVER',
    'SILVERM',
    'NATURALGAS',
    'NATGASMINI',
  ].map((name) => ({ venue: 'MCX' as const, name })),
  { venue: 'NFO' as const, name: 'NIFTY' },
];

export type ExpiryContract = (typeof EXPIRY_CONTRACTS)[number];

export function tenderFrom(
  calendar: MarketCalendar,
  venue: Venue,
  contract: string,
  expiry: string,
): string | null {
  const sessions = tenderSessions(venue, contract);
  if (sessions === 0) return null;
  let day: string | null = expiry;
  for (let step = 1; step < sessions && day !== null; step += 1) {
    day = previousTradingDay(calendar, venue, day);
  }
  return day;
}
